import { useState, useCallback } from 'react';
import { Search, MessageCircle, Settings, User, Star, Heart, ShieldBan } from 'lucide-react';
import { parseDisplayName } from '@/lib/utils';
import type { ConversationStatus } from '@/types/outreach';
import type { InboxHooks, InboxPlatformConfig, InboxStore, GenericConversation } from '../types';
import styles from './GenericConversationList.module.css';

interface Props {
  hooks: InboxHooks;
  store: InboxStore;
  platform: InboxPlatformConfig;
}

const STATUS_TABS: { label: string; value: ConversationStatus | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Handoff', value: 'HANDOFF' },
  { label: 'Closed', value: 'CLOSED' },
];

function formatTime(timestamp: string | null): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#ff9800',
  '#ff5722',
  '#795548',
  '#607d8b',
];

function getAvatarColor(identifier: string): string {
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getStatusColorClass(status: ConversationStatus): string {
  switch (status) {
    case 'ACTIVE':
      return styles.statusActive;
    case 'HANDOFF':
      return styles.statusHandoff;
    case 'PAUSED':
      return styles.statusPaused;
    case 'CLOSED':
      return styles.statusClosed;
    default:
      return '';
  }
}

export function GenericConversationList({ hooks, store, platform }: Props) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | ''>('');
  const [interestedFilter, setInterestedFilter] = useState<string>('');
  const [starredFilter, setStarredFilter] = useState(false);

  const { selectedConversationId, setSelectedConversationId, toggleAgentConfig } = store;
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    hooks.useConversations(
      search,
      statusFilter,
      interestedFilter || undefined,
      starredFilter || undefined,
    );
  const toggleInterest = hooks.useToggleInterest?.();
  const toggleStar = hooks.useToggleStar?.();

  const conversations = data?.pages.flatMap((p) => p.results) ?? [];
  const totalCount = data?.pages[0]?.count ?? 0;

  const getPreviewText = useCallback((conv: GenericConversation) => {
    if (!conv.lastMessage) return 'No messages yet';
    const { content, messageType, senderType } = conv.lastMessage;
    const prefix = senderType === 'AI_AGENT' ? 'AI: ' : senderType === 'ADMIN' ? 'You: ' : '';

    // Platform-specific message type previews
    switch (messageType) {
      case 'AUDIO':
        return `${prefix}Voice message`;
      case 'IMAGE':
        return `${prefix}Photo`;
      case 'VIDEO':
        return `${prefix}Video`;
      case 'DOCUMENT':
        return `${prefix}Document`;
      case 'LOCATION':
        return `${prefix}Location`;
      case 'STICKER':
        return `${prefix}Sticker`;
      case 'FILE':
        return `${prefix}File`;
      case 'POSTBACK':
        return `${prefix}Postback`;
      case 'STORY_REPLY':
        return `${prefix}Replied to your story`;
      case 'STORY_MENTION':
        return `${prefix}Mentioned you in their story`;
      case 'MEDIA_SHARE':
        return `${prefix}Shared media`;
      default:
        return `${prefix}${content || ''}`;
    }
  }, []);

  const PlatformIcon = platform.icon;

  // Starred empty state
  const showStarredEmpty = starredFilter && !isLoading && conversations.length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <PlatformIcon className={styles.platformIcon} />
          <h2 className={styles.title}>{platform.label}</h2>
        </div>
        <button
          onClick={toggleAgentConfig}
          className={styles.settingsBtn}
          title="AI Agent Settings"
        >
          <Settings className={styles.settingsIcon} />
        </button>
      </div>

      <div className={styles.searchBar}>
        <Search className={styles.searchIcon} />
        <input
          type="text"
          placeholder={platform.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tabs}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`${styles.tab} ${statusFilter === tab.value ? styles.tabActive : ''}`}
          >
            {tab.label}
          </button>
        ))}
        <button
          onClick={() => setInterestedFilter(interestedFilter === 'true' ? '' : 'true')}
          className={`${styles.tab} ${interestedFilter === 'true' ? styles.tabInterested : ''}`}
          title="Filter interested"
        >
          <Heart className={styles.tabStarIcon} />
        </button>
      </div>

      {/* Starred filter toggle — only shown when platform supports starring */}
      {toggleStar && (
        <div className={styles.starFilterRow}>
          <button
            onClick={() => setStarredFilter(false)}
            className={`${styles.starFilterBtn} ${!starredFilter ? styles.starFilterBtnActive : ''}`}
            aria-pressed={!starredFilter}
          >
            All
          </button>
          <button
            onClick={() => setStarredFilter(true)}
            className={`${styles.starFilterBtn} ${starredFilter ? styles.starFilterBtnActive : ''}`}
            aria-pressed={starredFilter}
          >
            <Star className={styles.starFilterIcon} />
            Starred
          </button>
        </div>
      )}

      <div className={styles.list}>
        {isLoading && (
          <div className={styles.skeletons}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className={styles.skeleton}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonContent}>
                  <div className={styles.skeletonTop}>
                    <div className={styles.skeletonName} />
                    <div className={styles.skeletonTime} />
                  </div>
                  <div className={styles.skeletonPreview} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && !showStarredEmpty && conversations.length === 0 && (
          <div className={styles.empty}>
            <MessageCircle className={styles.emptyIcon} />
            <p>No conversations found</p>
          </div>
        )}

        {showStarredEmpty && (
          <div className={styles.starredEmpty}>
            <Star className={styles.starredEmptyIcon} />
            <p className={styles.starredEmptyHeadline}>No starred chats yet</p>
            <p className={styles.starredEmptySub}>
              Tap the star on any conversation to keep it one click away. Use it for VIPs, hot
              leads, or chats you'll come back to.
            </p>
            <button onClick={() => setStarredFilter(false)} className={styles.starredEmptyCta}>
              Go to all chats
            </button>
          </div>
        )}

        {conversations.map((conv) => {
          const name =
            conv.customerName ||
            (conv.place ? parseDisplayName(conv.place.displayName)?.text : null);
          const hasName = !!name;
          const displayName = name || 'No Name';
          const hasPic = !!conv.profilePicUrl;
          const hasUnread = conv.unreadCount > 0;
          // Avatar seed: prefer primaryId for consistent color
          const avatarSeed = conv.primaryId || conv.id;
          const isStarred = conv.isStarred ?? false;

          return (
            <button
              key={conv.id}
              onClick={() => setSelectedConversationId(conv.id)}
              className={`${styles.card} ${selectedConversationId === conv.id ? styles.cardSelected : ''} ${conv.isPhoneBlacklisted ? styles.blockedRow : ''}`}
            >
              {/* Avatar */}
              <div className={styles.avatarWrapper}>
                {hasPic ? (
                  <img
                    src={conv.profilePicUrl!}
                    alt={displayName}
                    className={styles.avatarImg}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                      (e.currentTarget.nextElementSibling as HTMLElement | null)?.removeAttribute(
                        'style',
                      );
                    }}
                  />
                ) : null}
                <div
                  className={styles.avatarFallback}
                  style={{
                    background: getAvatarColor(avatarSeed),
                    display: hasPic ? 'none' : 'flex',
                  }}
                >
                  {hasName ? (
                    <span className={styles.avatarInitials}>{getInitials(name)}</span>
                  ) : (
                    <User className={styles.avatarIcon} />
                  )}
                </div>
                <span className={`${styles.avatarStatus} ${getStatusColorClass(conv.status)}`} />
                {conv.isPhoneBlacklisted && (
                  <ShieldBan
                    size={14}
                    className={styles.blockedBadge}
                    aria-label="Blocked number"
                  />
                )}
              </div>

              {/* Content */}
              <div className={styles.cardContent}>
                <div className={styles.cardTop}>
                  <span className={`${styles.cardName} ${hasUnread ? styles.cardNameBold : ''}`}>
                    {displayName}
                  </span>
                  {toggleInterest && (
                    <span
                      className={`${styles.interestedTag} ${conv.person?.isInterested ? styles.interestedTagActive : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleInterest.mutate(conv.id);
                      }}
                      title={conv.person?.isInterested ? 'Quitar interesado' : 'Marcar interesado'}
                    >
                      <Heart className={styles.interestedTagIcon} />
                    </span>
                  )}
                  {toggleStar && (
                    <button
                      type="button"
                      className={`${styles.starBtn} ${isStarred ? styles.starBtnActive : ''}`}
                      aria-label={isStarred ? 'Unstar conversation' : 'Star conversation'}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStar.mutate({ conversationId: conv.id, isStarred: !isStarred });
                      }}
                    >
                      <Star
                        className={styles.starIcon}
                        style={
                          isStarred
                            ? { fill: 'var(--color-warning)', color: 'var(--color-warning)' }
                            : undefined
                        }
                      />
                    </button>
                  )}
                  <span className={`${styles.cardTime} ${hasUnread ? styles.cardTimeUnread : ''}`}>
                    {formatTime(conv.lastMessage?.timestamp ?? conv.updatedAt)}
                  </span>
                </div>
                {/* Primary identifier (phone / @username / PSID) */}
                <span className={styles.cardPrimaryId}>{conv.primaryId}</span>
                {/* Secondary identifier (e.g. @username for Instagram alongside name) */}
                {conv.secondaryId && (
                  <span className={styles.cardSecondaryId}>{conv.secondaryId}</span>
                )}
                <div className={styles.cardBottom}>
                  <span
                    className={`${styles.cardPreview} ${hasUnread ? styles.cardPreviewUnread : ''}`}
                  >
                    {getPreviewText(conv)}
                  </span>
                  {hasUnread && <span className={styles.badge}>{conv.unreadCount}</span>}
                </div>
              </div>
            </button>
          );
        })}

        {hasNextPage && (
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className={styles.loadMore}
          >
            {isFetchingNextPage
              ? 'Loading...'
              : `Load more (${conversations.length} of ${totalCount})`}
          </button>
        )}
      </div>
    </div>
  );
}
